import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { invokeEdgeWithAuthRetry } from '@/lib/invoke-edge-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Mail, RefreshCw } from 'lucide-react';

type NonEnrolledAdmin = {
  wpUserId: number;
  email: string | null;
  displayName: string | null;
  lastReminderAt: string | null;
};

type MfaOutreachResponse = {
  pending: boolean;
  mfaCoverage?: {
    adminTotal?: number;
    adminEnrolled?: number;
    coveragePercent?: number;
    provider?: string;
    lastCheckedAt?: string | null;
  };
  nonEnrolledAdmins?: NonEnrolledAdmin[];
  listCommand?: {
    id?: string;
    status?: string;
    error?: string | null;
    created_at?: string;
    completed_at?: string | null;
  } | null;
  latestSendCommand?: {
    id?: string;
    status?: string;
    error?: string | null;
    created_at?: string;
    completed_at?: string | null;
  } | null;
};

type Props = {
  domainId: string;
  enabled: boolean;
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return 'Unknown';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 'Unknown';
  return new Date(parsed).toLocaleString();
};

export function AdminMfaOutreachCard({ domainId, enabled }: Props) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery<MfaOutreachResponse>({
    queryKey: ['admin-domain-mfa-outreach', domainId],
    queryFn: async () => {
      const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-domain-mfa-outreach', {
        body: {
          action: 'status',
          domainId,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as MfaOutreachResponse;
    },
    enabled: enabled && Boolean(domainId),
  });

  const nonEnrolledAdmins = data?.nonEnrolledAdmins || [];
  const allSelected = nonEnrolledAdmins.length > 0 && selectedIds.length === nonEnrolledAdmins.length;

  useEffect(() => {
    setSelectedIds(nonEnrolledAdmins.map((row) => row.wpUserId));
  }, [domainId, nonEnrolledAdmins]);

  const selectedCount = selectedIds.length;

  const coverageLabel = useMemo(() => {
    const total = Number(data?.mfaCoverage?.adminTotal ?? 0);
    const enrolled = Number(data?.mfaCoverage?.adminEnrolled ?? 0);
    const percent = Number(data?.mfaCoverage?.coveragePercent ?? 0);
    if (total <= 0) return 'Admins with MFA: Unknown';
    return `Admins with MFA: ${enrolled}/${total} (${percent.toFixed(1)}%)`;
  }, [data?.mfaCoverage]);

  const handleRefresh = async () => {
    if (!enabled || refreshing) return;
    setRefreshing(true);
    try {
      const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-domain-mfa-outreach', {
        body: {
          action: 'status',
          domainId,
          refresh: true,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success('Connector refresh queued for MFA user list.');
      await refetch();
    } catch (err: any) {
      console.error('admin-domain-mfa-outreach status refresh failed:', err);
      toast.error(err?.message || 'Unable to refresh MFA outreach status');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSend = async () => {
    if (!enabled || sending) return;
    if (selectedIds.length === 0) {
      toast.error('Select at least one user before sending.');
      return;
    }

    setSending(true);
    try {
      const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-domain-mfa-outreach', {
        body: {
          action: 'send',
          domainId,
          wpUserIds: selectedIds,
          cooldownDays: 30,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Queued MFA setup emails for ${selectedIds.length} user(s).`);
      await refetch();
    } catch (err: any) {
      console.error('admin-domain-mfa-outreach send failed:', err);
      toast.error(err?.message || 'Unable to queue MFA setup emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-border/60">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">WordPress MFA Outreach</CardTitle>
        <CardDescription>
          Review connector-reported non-MFA admins and send setup reminders in bulk.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {!enabled ? (
          <div className="text-muted-foreground">
            MFA outreach is currently disabled by rollout settings.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={data?.pending ? 'warning' : 'outline'}>
                {data?.pending ? 'Refreshing from connector' : 'Ready'}
              </Badge>
              <span className="text-muted-foreground">{coverageLabel}</span>
            </div>
            <div className="text-muted-foreground">
              Provider: {data?.mfaCoverage?.provider || 'Unknown'} · Last checked:{' '}
              {formatTimestamp(data?.mfaCoverage?.lastCheckedAt)}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void handleRefresh()} disabled={refreshing || isFetching}>
                {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                <span className="ml-1">Refresh List</span>
              </Button>
              <Button
                size="sm"
                onClick={() => void handleSend()}
                disabled={sending || isLoading || selectedCount === 0 || nonEnrolledAdmins.length === 0}
              >
                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                <span className="ml-1">Send Setup Emails ({selectedCount})</span>
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading outreach status...
              </div>
            ) : nonEnrolledAdmins.length === 0 ? (
              <div className="text-muted-foreground">
                {data?.pending
                  ? 'Waiting for connector to return non-enrolled admin details.'
                  : 'No non-enrolled admins reported.'}
              </div>
            ) : (
              <div className="rounded-md border border-border/50 overflow-hidden">
                <div className="grid grid-cols-[36px_1fr_1fr_1fr] gap-2 bg-muted/40 px-2 py-2 text-[11px] font-medium">
                  <div>
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedIds(nonEnrolledAdmins.map((row) => row.wpUserId));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                    />
                  </div>
                  <div>Email</div>
                  <div>Display Name</div>
                  <div>Last Reminder</div>
                </div>
                <div className="max-h-56 overflow-y-auto">
                  {nonEnrolledAdmins.map((row) => {
                    const checked = selectedIds.includes(row.wpUserId);
                    return (
                      <div
                        key={row.wpUserId}
                        className="grid grid-cols-[36px_1fr_1fr_1fr] gap-2 border-t border-border/40 px-2 py-2"
                      >
                        <div>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              if (value) {
                                setSelectedIds((prev) => Array.from(new Set([...prev, row.wpUserId])));
                              } else {
                                setSelectedIds((prev) => prev.filter((id) => id !== row.wpUserId));
                              }
                            }}
                          />
                        </div>
                        <div className="break-all">{row.email || 'No email'}</div>
                        <div>{row.displayName || 'Unknown'}</div>
                        <div>{formatTimestamp(row.lastReminderAt)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data?.latestSendCommand ? (
              <div className="text-muted-foreground">
                Last send command: {data.latestSendCommand.status || 'unknown'}
                {data.latestSendCommand.completed_at
                  ? ` · Completed ${formatTimestamp(data.latestSendCommand.completed_at)}`
                  : ''}
                {data.latestSendCommand.error ? ` · Error: ${data.latestSendCommand.error}` : ''}
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
